<template>
  <div class="cell object-details-card object-details-mobile-dense">
    <div class="grid-y divide-y">
      <div class="cell p-8 object-details-summary">
        <div class="grid-x object-details-summary-grid">
          <div class="cell auto">
            <div class="flex flex-row items-start">
              <div class="flex-shrink"></div>
              <div class="flex-grow flex flex-col">
                <h3 class="text-base font-bold mb-3">О захоронении</h3>
                <div class="flex flex-col space-y-2 object-details-summary-fields">
                  <div class="flex flex-col object-details-field">
                    <small class="text-muted">Номер участка:</small>
                    <b>{{ $delve(payload, 'section') || 'Не указан' }}</b>
                  </div>
                  <div class="flex flex-col object-details-field">
                    <small class="text-muted">Номер могилы:</small>
                    <b>{{ $delve(payload, 'crypt') || 'Не указан' }}</b>
                  </div>
                  <div class="flex flex-col object-details-field">
                    <small class="text-muted">Размер захоронения:</small>
                    <b v-html="$delve(payload, 'size') || 'Не указан'"></b>
                  </div>
                  <div class="flex flex-col object-details-field">
                    <small class="text-muted">Наличие ограды и ее тип:</small>
                    <b v-html="$delve(payload, 'fence') || 'Не указан'"></b>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="cell auto">
            <div class="flex flex-row items-start">
              <div class="flex-shrink"></div>
              <div class="flex-grow flex flex-col items-start space-y-6">
                <div class="flex flex-col space-y-2 object-location-block">
                  <h3 class="text-base font-bold">Местоположение</h3>
                  <div class="flex flex-col space-y-1">
                    <small class="text-muted">{{ location }}</small>
                    <b class="text-12px leading-14px">{{ $delve(payload, 'cemetery.name') }}</b>
                    <div
                      v-if="payload.location"
                      class="flex flex-row space-x-4 object-location-coordinates">
                      <span>
                        <span class="font-bold">Широта:</span> {{ payload.location.lat }}
                      </span>
                      <span>
                        <span class="font-bold">Долгота:</span> {{ payload.location.lng }}
                      </span>
                    </div>
                  </div>
                  <div
                    class="rounded-lg overflow-hidden"
                    v-if="$delve(payload, 'cemetery.mapAttach')"
                    @click="mapAttachLightbox = !mapAttachLightbox">
                    <Attachment
                      class="max-w-full w-80 cursor-pointer"
                      :id="$delve(payload, 'cemetery.mapAttach')"/>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="cell p-8 space-y-8 object-burials">
        <div
          class="flex flex-col flex-start space-y-6 object-burial-card"
          v-for="(burial, index) in _burials"
          :key="index">
          <div class="flex flex-col space-y-4">
            <b class="mb-2">
              <span class="text-16px leading-18px title-divided self-start">
                Информация о захоронении
              </span>
              <span
                class="text-16px leading-18px title-divided self-start"
                v-if="payload.burials.length > 1">
                ({{ parseInt(index, 10) + 1 }})
              </span>
            </b>
            <h3 class="text-24px leading-28px font-bold">{{ $delve(burial, 'primary.name') }}</h3>
            <div class="flex flex-row items-center space-x-2">
              <span class="text-14px leading-17px">Даты жизни:</span>
              <span class="badge badge-zircon text-inverse text-14px leading-17px font-rubik">
                {{ $delve(burial, 'primary.birthday') | date }}
                -
                {{ $delve(burial, 'primary.deathday') | date }} г.
              </span>
            </div>
            <div class="flex flex-col space-y-2 burial-summary-fields">
              <div class="flex flex-col object-details-field">
                <small class="text-muted">Памятник:</small>
                <b>{{ $delve(burial, 'headstone') || 'Не указан' }}</b>
              </div>
              <div class="flex flex-col object-details-field">
                <small class="text-muted">Наличие и тип цоколя:</small>
                <b>{{ $delve(burial, 'pedestal') || 'Не указан' }}</b>
              </div>
              <div class="flex flex-col object-details-field">
                <small class="text-muted">Наличие и тип цветника:</small>
                <b>{{ $delve(burial, 'flowerGarden') || 'Не указан' }}</b>
              </div>
            </div>
          </div>
          <div
            class="flex flex-col space-y-4"
            v-if="burial.secondary.length">
            <b class="mb-2">
              <span class="text-16px leading-18px title-divided self-start">
                Информация о подзахоронениях
              </span>
            </b>
            <div
              v-for="(secondary, secondaryIndex) in burial.secondary"
              :key="secondaryIndex"
              class="flex flex-col space-y-4">
              <h3 class="text-24px leading-28px font-bold">
                {{ secondary.name }}
              </h3>
              <div class="flex flex-row items-center space-x-2">
                <span class="text-14px leading-17px">Даты жизни:</span>
                <span class="badge badge-zircon text-inverse text-14px leading-17px font-rubik">
                    {{ secondary.birthday | date }}
                    -
                    {{ secondary.deathday | date }} г.
                  </span>
              </div>
            </div>
          </div>
          <div
            class="flex flex-col space-y-4"
            v-if="burial.photoAttaches && burial.photoAttaches.length">
            <b class="mb-2">
              <span class="text-16px leading-18px title-divided self-start">
                Фото захоронения
              </span>
            </b>
            <div class="flex flex-row flex-wrap -m-2">
              <div
                class="w-24 h-32 overflow-hidden rounded-lg flex-shrink-0 m-2 cursor-pointer"
                v-for="(attach, attachIndex) in burial.photoAttaches"
                :key="`${attach}${attachIndex}`"
                @click="openPhotoAttaches(index, attachIndex)">
                  <Attachment
                    class="h-full object-center object-cover w-full"
                    :id="attach"/>
                </div>
            </div>
          </div>
          <div
            class="flex flex-row items-start"
            v-if="$delve(burial, 'comment')">
            <div class="flex flex-col"></div>
            <div class="flex flex-col space-y-2">
              <h3 class="text-16px leading-18px font-bold">Комментарий заказчика</h3>
              <small class="text-12px leading-16px text-muted">
                {{ $delve(burial, 'comment') }}
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Lightbox
      :toggler="mapAttachLightbox"
      :sources="[$delve(payload, 'cemetery.mapAttach')]"/>
    <Lightbox
      :toggler="photoAttachesLightbox[index]"
      :sources="burial.photoAttaches"
      v-for="(burial, index) in payload.burials"
      :key="index"/>
  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import { mapGetters } from 'vuex';
import Lightbox from '@/components/global/Lightbox.vue';
import Attachment from '@/components/global/Attachment.vue';

export default {
  props: {
    subject: {
      type: Object,
      validator: (obj) => typeof obj === 'object' || obj === null,
    },
  },
  components: {
    Lightbox,
    Attachment,
  },
  data() {
    return {
      mapAttachLightbox: false,
      photoAttachesLightbox: [],
      photoAttachesIndex: [],
    };
  },
  computed: {
    ...mapGetters({
      enumsGetter: 'settings/selectEnums',
    }),
    payload() {
      const { subject } = this;
      return {
        ...subject,
        fence: _.get(
          _.find(this.fences, (obj) => obj.value === subject.fence) || {},
          'label',
        ),
        size: _.get(
          _.find(this.sizes, (obj) => obj.value === subject.size) || {},
          'label',
        ),
        flowerGarden: _.get(
          _.find(this.flowerGardens, (obj) => obj.value === subject.flowerGarden) || {},
          'label',
        ),
        headstone: _.get(
          _.find(this.headstones, (obj) => obj.value === subject.headstone) || {},
          'label',
        ),
        pedestal: _.get(
          _(this.pedestals)
            .find((obj) => obj.value === subject.pedestal) || {},
          'label',
        ),
        burials: subject.burials.map((burial) => {
          const flowerGarden = _.find(this.flowerGardens, (o) => o.value === burial.flowerGarden);
          const headstone = _.find(this.headstones, (o) => o.value === burial.headstone);
          const pedestal = _.find(this.pedestals, (o) => o.value === burial.pedestal);
          return {
            ...burial,
            flowerGarden: _.get(flowerGarden, 'label') || null,
            headstone: _.get(headstone, 'label') || null,
            pedestal: _.get(pedestal, 'label') || null,
          };
        }),
      };
    },
    location() {
      return [
        _.get(this.payload, 'cemetery.district.region.country.name'),
        _.get(this.payload, 'cemetery.district.region.name'),
        _.get(this.payload, 'cemetery.district.name'),
      ].join(', ');
    },
    sizes() {
      return this.enumsGetter('size');
    },
    fences() {
      return this.enumsGetter('fence');
    },
    headstones() {
      return this.enumsGetter('headstone');
    },
    pedestals() {
      return this.enumsGetter('pedestal');
    },
    flowerGardens() {
      return this.enumsGetter('flowerGarden');
    },
    // eslint-disable-next-line no-underscore-dangle
    _burials() {
      return this.payload?.burials.map((burial) => {
        // eslint-disable-next-line no-param-reassign,prefer-destructuring
        burial.primary = burial.persons[0];
        // eslint-disable-next-line no-param-reassign
        burial.secondary = burial.persons.slice(1);
        return burial;
      });
    },
  },
  filters: {
    date(s) {
      const date = moment(s);
      if (date.isValid()) {
        // eslint-disable-next-line no-underscore-dangle
        let { _f } = date;
        if (_f) {
          _f = _f.split('-').reverse().join('.');
          return date.format(_f);
        }
      }
      return null;
    },
  },
  methods: {
    openPhotoAttaches(burialIndex, index) {
      this.$set(this.photoAttachesIndex, burialIndex, index);
      this.$set(this.photoAttachesLightbox, burialIndex, !this.photoAttachesLightbox[burialIndex]);
    },
  },
};
</script>
